import { Col, Row } from 'antd';
import { useCallback } from 'react';

import type {
  IWorkJourneyManagementCalendarCell,
  IWorkJourneyManagementCalendarMonthSummary,
} from '@src/models/interfaces/workJourneyManagement.interface';

import { WorkJourneyManagementEmptyState } from '../WorkJourneyManagementEmptyState';
import { WorkJourneyMonthSidebar } from '../WorkJourneyMonthSidebar';
import { WorkJourneyCalendarDayDetailDrawer } from './components/WorkJourneyCalendarDayDetailDrawer';
import { WorkJourneyCalendarDayReportDrawer } from './components/WorkJourneyCalendarDayReportDrawer';
import { WorkJourneyCalendarGrid } from './components/WorkJourneyCalendarGrid';
import { useWorkJourneyCalendarDayDrawer } from './hooks/useWorkJourneyCalendarDayDrawer';
import { useWorkJourneyCalendarDayReportDrawer } from './hooks/useWorkJourneyCalendarDayReportDrawer';
import styles from './styles.module.less';

const CALENDAR_SIDEBAR_ISSUE_TYPES = [
  'below_minimum',
  'scheduled_absences',
  'rejections',
  'holiday',
] as const;

interface WorkJourneyCalendarSectionProps {
  summary: IWorkJourneyManagementCalendarMonthSummary | null;
  cells: IWorkJourneyManagementCalendarCell[];
  loading?: boolean;
  onPreviousMonth: () => void;
  onNextMonth: () => void;
}

export const WorkJourneyCalendarSection = ({
  summary,
  cells,
  loading = false,
  onPreviousMonth,
  onNextMonth,
}: WorkJourneyCalendarSectionProps) => {
  const dayDrawer = useWorkJourneyCalendarDayDrawer(cells);
  const reportDrawer = useWorkJourneyCalendarDayReportDrawer();
  const { openForCell: openDayConflicts } = dayDrawer;
  const { openForCell: openDayReport } = reportDrawer;

  const handleDayFooterClick = useCallback(
    (cell: IWorkJourneyManagementCalendarCell) => {
      if (cell.footerAction === 'report') {
        openDayReport(cell);
        return;
      }
      if (cell.journeyCount === 0 || !(cell.conflicts?.length > 0)) {
        return;
      }
      openDayConflicts(cell);
    },
    [openDayConflicts, openDayReport],
  );

  if (!summary) {
    return loading ? null : (
      <WorkJourneyManagementEmptyState messageId="workJourney.management.empty.calendar" />
    );
  }

  return (
    <section className={loading ? styles.loading : undefined}>
      <Row gutter={[24, 24]} align="top">
        <Col xs={24} lg={6}>
          <WorkJourneyMonthSidebar
            month={summary.month}
            year={summary.year}
            issues={summary.issues}
            issueTypes={[...CALENDAR_SIDEBAR_ISSUE_TYPES]}
            issueLabelPrefix="workJourney.management.calendar.sidebar.issue"
            onPreviousMonth={onPreviousMonth}
            onNextMonth={onNextMonth}
          />
        </Col>
        <Col xs={24} lg={18} className={styles.gridCol}>
          <WorkJourneyCalendarGrid cells={cells} onDayFooterClick={handleDayFooterClick} />
        </Col>
      </Row>

      <WorkJourneyCalendarDayDetailDrawer
        open={dayDrawer.open}
        date={dayDrawer.date}
        items={dayDrawer.items}
        loading={dayDrawer.loading}
        error={dayDrawer.error}
        onClose={dayDrawer.close}
      />

      <WorkJourneyCalendarDayReportDrawer
        open={reportDrawer.open}
        date={reportDrawer.date}
        journeys={reportDrawer.journeys}
        summary={reportDrawer.summary}
        totalJourneys={reportDrawer.totalJourneys}
        page={reportDrawer.page}
        pageSize={reportDrawer.pageSize}
        loading={reportDrawer.loading}
        error={reportDrawer.error}
        expandedJourneyId={reportDrawer.expandedJourneyId}
        detailByJourneyId={reportDrawer.detailByJourneyId}
        onClose={reportDrawer.close}
        onPageChange={reportDrawer.setPage}
        onToggleJourney={reportDrawer.toggleJourneyExpand}
      />
    </section>
  );
};
